import React, { useState, useEffect } from 'react';
import RichTextEditor from './RichTextEditor';
import 'react-quill/dist/quill.snow.css';
import {
  Box, Grid, TextField, Button, Typography, Divider, Paper,
  Stepper, Step, StepLabel, FormControl, InputLabel, Select, MenuItem, Stack, Autocomplete, useTheme, alpha, IconButton, Avatar,
  Checkbox, FormControlLabel, Chip, ToggleButton, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, CircularProgress
} from '@mui/material';
import {
  MdVisibility,
  MdVisibilityOff,
  MdImage,
  MdLocalOffer,
  MdCategory,
  MdCurrencyRupee,
  MdInfo,
  MdScale,
  MdSchedule,
  MdStar,
  MdChevronLeft,
  MdChevronRight,
  MdShoppingBag,
  MdSettings,
  MdCheckCircle
} from 'react-icons/md';
import { FaEdit, FaEye } from 'react-icons/fa';
import { FiX, FiType, FiUploadCloud, FiTrash2, FiCircle, FiLayers } from 'react-icons/fi';
import { postFileUpload, getData, postData } from '../../Webservice/Webservice';
import CATEGORY_ROUTES from '../../API_Route/CategoryRoute';
import SUBCATEGORY_ROUTES from '../../API_Route/SubCatagoryRoute';

const BADGE_OPTIONS = ['New Arrival', 'Best Seller', 'Editor\'s Choice', 'Limited Edition', 'Top Rated', 'Hot Deal', 'Organic'];
const SKIN_TYPES = ['All Skin Types', 'Oily', 'Dry', 'Combination', 'Sensitive', 'Normal', 'Acne Prone'];
const HAIR_TYPES = ['All Hair Types', 'Oily', 'Dry', 'Normal', 'Color Treated', 'Curly', 'Straight', 'Thinning'];
const SHIPPING_CLASSES = ['Standard Shipping', 'Express Shipping', 'Next Day Delivery', 'Free Shipping', 'Heavy Item'];
const RETURN_POLICIES = ['7 Days Return', '15 Days Return', '30 Days Return', 'No Return', 'Exchange or Refund'];


const steps = ['Basic Information', 'Product Details', 'Price Details', 'Dimensions', 'Additional Information', 'Review'];

// Quill editor configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'align': [] }],
    ['link', 'image'],
    ['clean']
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'color', 'background',
  'align',
  'link', 'image'
];

const ProductForm = ({
  form,
  onFormChange,
  onCancel,
  onSubmit,
  isSubmitDisabled,
  categories = [],
  subcategories = [],
  isEdit = false,
  formMode = 'edit',
  handleFormModeChange = null,
  fetchCategories,
  fetchSubcategories
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [titleChecking, setTitleChecking] = useState(false);
  const [brandOptions, setBrandOptions] = useState([]);

  // Fetch active brands from the Brand master on mount
  useEffect(() => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    const authToken = token ? `Bearer ${token}` : '';
    getData('brand/all', { limit: 200 }, authToken, false)
      .then((res) => {
        if (res?.success && Array.isArray(res.brands)) {
          setBrandOptions(res.brands.map((b) => b.name || b));
        }
      })
      .catch(() => {});
  }, []);

  const validateStep = (step) => {
    if (formMode === 'view') return true;
    const newErrors = {};
    if (step === 0) {
      if (!form.title) newErrors.title = 'Title is required';
      if (!form.description) newErrors.description = 'Description is required';
      if (!form.category_id) newErrors.category_id = 'Category is required';
      if (!form.subcategory_id) newErrors.subcategory_id = 'Subcategory is required';
      if (!form.brand) newErrors.brand = 'Brand is required';
    } else if (step === 1) {
      const isRichTextEmpty = (html) => {
        if (!html) return true;
        const text = html.replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, '').trim();
        return text === '' && !html.includes('<img');
      };

      if (isRichTextEmpty(form.key_features)) newErrors.key_features = 'Key Features are required';
      if (isRichTextEmpty(form.how_to_use)) newErrors.how_to_use = 'How to Use instructions are required';
      if (!form.unitType) newErrors.unitType = 'Unit Type is required';
      if (!form.unitValue) newErrors.unitValue = 'Unit Value is required';
      if (form.unitType === 'weight' && !form.unitName) newErrors.unitName = 'Unit Name is required for weight';
      if (!form.product_images || form.product_images.length === 0) newErrors.product_images = 'At least one product image is required';
    } else if (step === 2) {
      if (!form.isFree) {
        if (!form.b2c_mrp) newErrors.b2c_mrp = 'MRP is required';
        if (!form.gst) newErrors.gst = 'GST is required';
        
        const visibility = Array.isArray(form.visibility) ? form.visibility : (form.visibility ? [form.visibility] : ['b2b', 'b2c']);
        if (visibility.includes('b2b')) {
          if (!form.b2b_sellingPrice) newErrors.b2b_sellingPrice = 'B2B Selling Price is required';
        }
        if (visibility.includes('b2c')) {
          if (!form.b2c_sellingPrice) newErrors.b2c_sellingPrice = 'B2C Selling Price is required';
        }
      } else {
        if (!form.thresholdAmount) newErrors.thresholdAmount = 'Threshold Amount is required for free products';
        if (!form.gst) newErrors.gst = 'GST is required';
      }
    } else if (step === 4) {
      if (!form.status) newErrors.status = 'Status is required';
      if (!form.ideal_for) newErrors.ideal_for = 'Ideal For is required';
      if (!form.container_type) newErrors.container_type = 'Container Type is required';
    }

    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      // Find the first error message to alert or focus
      const firstError = Object.values(newErrors)[0];
      // Optional: use a snackbar or more subtle way to show error
      return false;
    }
    return true;
  };

  const handleTitleBlur = async () => {
    if (!form.title || form.title.trim() === '') return;
    
    setTitleChecking(true);
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const response = await postData('product/check-duplicate-title', { 
        title: form.title,
        id: isEdit ? (form._id || null) : null
      }, token, false);

      if (response && response.exists) {
        setErrors(prev => ({ ...prev, title: 'This product title already exists.' }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.title;
          return newErrors;
        });
      }
    } catch (error) {
      console.error('Error checking title duplicate:', error);
    } finally {
      setTitleChecking(false);
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Add Category Modal State
  const [addOpen, setAddOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ title: '', image: '', status: 'active', categoryCluster: '' });
  const [imagePreview, setImagePreview] = useState('');
  const [categoryClusters, setCategoryClusters] = useState([]);

  useEffect(() => {
      const fetchClusters = async () => {
          try {
              const tokenValue = sessionStorage.getItem('token');
              const token = tokenValue ? `Bearer ${tokenValue}` : '';
              const response = await getData('category-cluster/list?limit=1000', null, token);
              setCategoryClusters(response?.clusters || []);
          } catch (error) {
              console.error('Fetch clusters failed:', error);
          }
      };
      fetchClusters();
  }, []);

  const handleAddCategoryClose = () => {
    setAddOpen(false);
    setNewCategory({ title: '', image: '', status: 'active', categoryCluster: '' });
    setImagePreview('');
  };

  const handleAddCategorySubmit = async () => {
    if (!newCategory.categoryCluster) {
      alert("Category Cluster is mandatory");
      return;
    }
    try {
      const formData = new FormData();
      formData.append('title', newCategory.title);
      formData.append('status', newCategory.status);
      if (newCategory.categoryCluster) {
          formData.append('categoryCluster', newCategory.categoryCluster);
      }
      if (newCategory.image) {
        formData.append('image', newCategory.image);
      }
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      await postFileUpload(CATEGORY_ROUTES.ADD_CATEGORY, formData, token);
      handleAddCategoryClose();
      if (fetchCategories) {
        fetchCategories();
      }
    } catch (error) {
      console.error('Add category failed:', error);
    }
  };

  // Add Subcategory Modal State
  const [addSubOpen, setAddSubOpen] = useState(false);
  const [newSubcategory, setNewSubcategory] = useState({ title: '', image: '', status: 'active', category_id: '' });

  const handleAddSubCategoryClose = () => {
    setAddSubOpen(false);
    setNewSubcategory({ title: '', image: '', status: 'active', category_id: '' });
    setImagePreview('');
  };

  const handleAddSubcategorySubmit = async () => {
    try {
      const formData = new FormData();
      formData.append('title', newSubcategory.title);
      formData.append('status', newSubcategory.status);
      formData.append('category_id', newSubcategory.category_id);
      if (newSubcategory.image) {
        formData.append('image', newSubcategory.image);
      }
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      await postFileUpload(SUBCATEGORY_ROUTES.ADD_SUBCATEGORY, formData, token);

      // If the added subcategory belongs to the currently selected category in the form, refresh the list
      if (fetchSubcategories && newSubcategory.category_id === form.category_id) {
        fetchSubcategories(newSubcategory.category_id);
      }

      handleAddSubCategoryClose();
    } catch (error) {
      console.error('Add subcategory failed:', error);
    }
  };

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const calculateDiscountedPrice = () => {
    if (!form.b2c_discountType || !form.b2c_discountValue) return form.b2c_sellingPrice || form.b2c_mrp;

    const price = parseFloat(form.b2c_sellingPrice || form.b2c_mrp);
    const discount = parseFloat(form.b2c_discountValue);

    if (form.b2c_discountType === 'percentage') {
      return Math.round(price - (price * discount / 100));
    } else {
      return Math.round(price - discount);
    }
  };

  const theme = useTheme();

  const renderViewField = (label, value) => (
    <Box sx={{ p: 1 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography>{value || '-'}</Typography>
    </Box>
  );

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length) {
      // Store the actual File objects in the form state
      const existingImages = Array.isArray(form.product_images) ? form.product_images : [];
      const allImages = [...existingImages, ...files];
      onFormChange({
        target: { name: 'product_images', value: allImages }
      });
      // Clear the input value to allow uploading the same file again if removed
      e.target.value = null;
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    const updatedImages = form.product_images.filter((_, index) => index !== indexToRemove);
    onFormChange({
      target: { name: 'product_images', value: updatedImages }
    });
  };

  const calculateSellingPrice = () => {
    const mrp = parseFloat(form.b2c_mrp) || 0;
    const discountValue = parseFloat(form.b2c_discountValue) || 0;

    // If there's an existing selling price and no discount is being applied, return the existing price
    if (form.b2c_sellingPrice && (!form.b2c_discountType || !form.b2c_discountValue)) {
      return Math.round(parseFloat(form.b2c_sellingPrice) || 0).toString();
    }

    if (!form.b2c_discountType || !form.b2c_mrp) {
      return mrp.toFixed(0); // Return MRP if no discount or no MRP entered
    }

    if (form.b2c_discountType === 'flat') {
      return Math.max(0, mrp - discountValue).toFixed(0);
    }
    else if (form.b2c_discountType === 'percentage') {
      const discountAmount = mrp * (Math.min(100, discountValue) / 100);
      return Math.max(0, mrp - discountAmount).toFixed(0);
    }

    return mrp.toFixed(0);
  };

  const calculateB2BSellingPrice = () => {
    const mrp = parseFloat(form.b2c_mrp) || 0;
    const discountValue = parseFloat(form.b2b_discountValue) || 0;

    // If there's an existing selling price and no discount is being applied, return the existing price
    if (form.b2b_sellingPrice && (!form.b2b_discountType || !form.b2b_discountValue)) {
      return Math.round(parseFloat(form.b2b_sellingPrice) || 0).toString();
    }

    if (!form.b2b_discountType || !form.b2c_mrp) {
      return mrp.toFixed(0); // Return MRP if no discount or no MRP entered
    }

    if (form.b2b_discountType === 'flat') {
      return Math.max(0, mrp - discountValue).toFixed(0);
    }
    else if (form.b2b_discountType === 'percentage') {
      const discountAmount = mrp * (Math.min(100, discountValue) / 100);
      return Math.max(0, mrp - discountAmount).toFixed(0);
    }

    return mrp.toFixed(0);
  };


  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sx={{ mt: 2 }}>
              {formMode === 'view' ? (
                renderViewField('Title', form.title)
              ) : (
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  value={form.title}
                  onChange={onFormChange}
                  onBlur={handleTitleBlur}
                  variant="outlined"
                  required
                  error={!!errors.title}
                  helperText={errors.title || (titleChecking ? "Checking title availability..." : "")}
                  InputProps={{
                    endAdornment: titleChecking ? (
                      <InputAdornment position="end">
                        <CircularProgress size={20} color="inherit" />
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '&:hover fieldset': { borderColor: 'rgb(255 209 2)' },
                      '&.Mui-focused fieldset': { borderColor: 'rgb(255 209 2)' }
                    }
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('HSN Code', form.hsn_code)
              ) : (
                <TextField
                  fullWidth
                  label="HSN Code"
                  name="hsn_code"
                  value={form.hsn_code || ''}
                  onChange={onFormChange}
                  variant="outlined"
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('UPC', form.upc)
              ) : (
                <TextField
                  fullWidth
                  label="UPC"
                  name="upc"
                  value={form.upc}
                  onChange={onFormChange}
                  variant="outlined"
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('GSIN', form.gsin || 'N/A')
              ) : (
                <TextField
                  fullWidth
                  label="GSIN"
                  name="gsin"
                  value={form.gsin || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  helperText="System generated unique code"
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {(() => {
                const getVisibilityArray = (vis) => {
                  if (vis === undefined || vis === null) return ['b2b', 'b2c'];
                  if (Array.isArray(vis)) {
                    if (vis.length === 1 && typeof vis[0] === 'string' && vis[0].includes(',')) {
                      return vis[0].split(',').map(v => v.trim().toLowerCase());
                    }
                    return vis.map(v => typeof v === 'string' ? v.trim().toLowerCase() : v);
                  }
                  if (typeof vis === 'string') return vis.split(',').map(v => v.trim().toLowerCase());
                  return [];
                };
                const currentVisibility = getVisibilityArray(form.visibility);
                return formMode === 'view' ? (
                  renderViewField('Visibility', currentVisibility.length > 0 ? currentVisibility.map(v => v.toUpperCase()).join(', ') : 'Not selected')
                ) : (
                  <FormControl component="fieldset" fullWidth>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Visibility</Typography>
                    <Box display="flex" gap={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={currentVisibility.includes('b2c')}
                            onChange={(e) => {
                              const updated = e.target.checked 
                                ? [...currentVisibility, 'b2c']
                                : currentVisibility.filter(v => v !== 'b2c');
                              onFormChange({ target: { name: 'visibility', value: [...new Set(updated)] } });
                            }}
                          />
                        }
                        label="B2C"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={currentVisibility.includes('b2b')}
                            onChange={(e) => {
                              const updated = e.target.checked 
                                ? [...currentVisibility, 'b2b']
                                : currentVisibility.filter(v => v !== 'b2b');
                              onFormChange({ target: { name: 'visibility', value: [...new Set(updated)] } });
                            }}
                          />
                        }
                        label="B2B"
                      />
                    </Box>
                  </FormControl>
                );
              })()}
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('COD Available', form.codAvailable ? 'Yes' : 'No')
              ) : (
                <Box display="flex" alignItems="center" height="100%">
                  <Typography variant="body1" mr={2}>COD Available</Typography>
                  <input
                    style={{ transform: 'scale(1.5)', cursor: 'pointer' }}
                    type="checkbox"
                    name="codAvailable"
                    checked={!!form.codAvailable}
                    onChange={e => onFormChange({
                      target: {
                        name: 'codAvailable',
                        value: e.target.checked
                      }
                    })}
                  />
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? renderViewField('Manufacturer Name', form.manufacturerName) : (
                <TextField fullWidth label="Manufacturer Name" name="manufacturerName" value={form.manufacturerName || ''} onChange={onFormChange} variant="outlined" />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? renderViewField('Manufacturer Address', form.manufacturerAddress) : (
                <TextField fullWidth label="Manufacturer Address" name="manufacturerAddress" value={form.manufacturerAddress || ''} onChange={onFormChange} variant="outlined" />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? renderViewField('Manufacturing Licence No', form.manufacturingLicenseNumber) : (
                <TextField fullWidth label="Manufacturing Licence No" name="manufacturingLicenseNumber" value={form.manufacturingLicenseNumber || ''} onChange={onFormChange} variant="outlined" />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('Brand', form.brand)
              ) : (
                <Autocomplete
                  freeSolo
                  options={brandOptions}
                  value={form.brand || ''}
                  onChange={(_, newValue) =>
                    onFormChange({ target: { name: 'brand', value: newValue || '' } })
                  }
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === 'input') {
                      onFormChange({ target: { name: 'brand', value: newInputValue } });
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Brand"
                      name="brand"
                      variant="outlined"
                      required
                      error={!!errors.brand}
                      helperText={errors.brand || 'Select from master or type a new brand name'}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              {formMode === 'view' ? (
                renderViewField('Description', form.description)
              ) : (
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={form.description}
                  onChange={onFormChange}
                  variant="outlined"
                  multiline
                  rows={3}
                  required
                  error={!!errors.description}
                  helperText={errors.description}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                    }
                  }}
                />
              )}
            </Grid>
            {/*category */}
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('Category', categories.find(cat => cat._id === form.category_id)?.title || 'Not selected')
              ) : (
                <Autocomplete
                  options={categories}
                  getOptionLabel={(option) => option.title || ""}
                  value={categories.find(cat => cat._id === form.category_id) || null}
                  onChange={(event, newValue) => {
                    onFormChange({
                      target: {
                        name: "category_id",
                        value: newValue ? newValue._id : ""
                      }
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category"
                      required
                      fullWidth
                      placeholder="Search category..."
                      error={!!errors.category_id}
                      helperText={errors.category_id}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <MenuItem
                      {...props}
                      key={option._id}
                      style={{ height: 48 }} // Fixed menu item height
                    >
                      {option.title}
                    </MenuItem>
                  )}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  ListboxProps={{
                    style: {
                      maxHeight: 144, // 3 items * 48px each
                      overflow: 'auto',
                    }
                  }}
                  PaperComponent={({ children }) => (
                    <Paper style={{
                      marginTop: 8,
                      boxShadow: '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)'
                    }}>
                      {children}
                    </Paper>
                  )}
                  noOptionsText="No categories found"
                />
              )}
              {formMode !== 'view' && (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    display: "block",
                    textAlign: "right",
                    color: "primary.main",
                    cursor: "pointer",
                    fontSize: '0.9rem',
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                  onClick={() => setAddOpen(true)}
                >
                  Add New Category
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('Subcategory', subcategories.find(sub => sub._id === form.subcategory_id)?.title || 'Not selected')
              ) : (
                <Autocomplete
                  options={subcategories}
                  getOptionLabel={(option) => option.title || ""}
                  value={subcategories.find(sub => sub._id === form.subcategory_id) || null}
                  onChange={(event, newValue) => {
                    onFormChange({
                      target: {
                        name: "subcategory_id",
                        value: newValue ? newValue._id : ""
                      }
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Subcategory"
                      fullWidth
                      placeholder="Search subcategory..."
                      required
                      error={!!errors.subcategory_id}
                      helperText={errors.subcategory_id}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <MenuItem
                      {...props}
                      key={option._id}
                      style={{ height: 48 }} // Fixed menu item height
                    >
                      {option.title}
                    </MenuItem>
                  )}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  ListboxProps={{
                    style: {
                      maxHeight: 144, // 3 items * 48px each
                      overflow: 'auto',
                    }
                  }}
                  PaperComponent={({ children }) => (
                    <Paper style={{
                      marginTop: 8,
                      boxShadow: '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)'
                    }}>
                      {children}
                    </Paper>
                  )}
                  noOptionsText="No subcategories found"
                />
              )}
              {formMode !== 'view' && (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    display: "block",
                    textAlign: "right",
                    color: "primary.main",
                    cursor: "pointer",
                    fontSize: '0.9rem',
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                  onClick={() => {
                    setNewSubcategory(prev => ({ ...prev, category_id: form.category_id || '' }));
                    setAddSubOpen(true);
                  }}
                >
                  Add New SubCategory
                </Typography>
              )}
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            {/* Product Details fields (excluding price fields) */}
            <Grid item xs={12}>
              {formMode === 'view' ? (
                renderViewField('Key Features', form.key_features ? (
                  <div dangerouslySetInnerHTML={{ __html: form.key_features }} />
                ) : 'Not provided')
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Key Features
                  </Typography>
                  <Box sx={{
                    border: !!errors.key_features ? '1px solid #d32f2f' : '1px solid #ccc',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    '& .ql-container': {
                      minHeight: '120px',
                      fontSize: '14px'
                    },
                    '& .ql-toolbar': {
                      borderTop: 'none',
                      borderLeft: 'none',
                      borderRight: 'none',
                      borderBottom: '1px solid #ccc'
                    },
                    '& .ql-editor': {
                      minHeight: '100px'
                    }
                  }}>
                    <RichTextEditor
                      value={form.key_features || ''}
                      onChange={(content) => {
                        if (content !== (form.key_features || '')) {
                          onFormChange({
                            target: {
                              name: 'key_features',
                              value: content
                            }
                          });
                          if (errors.key_features) {
                            setErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.key_features;
                              return newErrors;
                            });
                          }
                        }
                      }}
                      placeholder="Enter key features of the product..."
                    />
                  </Box>
                  {errors.key_features && (
                    <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                      {errors.key_features}
                    </Typography>
                  )}
                </>
              )}
            </Grid>
            <Grid item xs={12}>
              {formMode === 'view' ? (
                renderViewField('How to Use', form.how_to_use ? (
                  <div dangerouslySetInnerHTML={{ __html: form.how_to_use }} />
                ) : 'Not provided')
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    How to Use
                  </Typography>
                  <Box sx={{
                    border: !!errors.how_to_use ? '1px solid #d32f2f' : '1px solid #ccc',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    '& .ql-container': {
                      minHeight: '120px',
                      fontSize: '14px'
                    },
                    '& .ql-toolbar': {
                      borderTop: 'none',
                      borderLeft: 'none',
                      borderRight: 'none',
                      borderBottom: '1px solid #ccc'
                    },
                    '& .ql-editor': {
                      minHeight: '100px'
                    }
                  }}>
                    <RichTextEditor
                      value={form.how_to_use || ''}
                      onChange={(content) => {
                        if (content !== (form.how_to_use || '')) {
                          onFormChange({
                            target: {
                              name: 'how_to_use',
                              value: content
                            }
                          });
                          if (errors.how_to_use) {
                            setErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.how_to_use;
                              return newErrors;
                            });
                          }
                        }
                      }}
                      placeholder="Enter instructions on how to use the product..."
                    />
                  </Box>
                  {errors.how_to_use && (
                    <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                      {errors.how_to_use}
                    </Typography>
                  )}
                </>
              )}
            </Grid>
            <Grid item xs={12}>
              {formMode === 'view' ? (
                renderViewField('Product Images', form.product_images?.length > 0 ? `${form.product_images.length} image(s) uploaded` : 'No images uploaded')
              ) : (
                <Box>
                  <Button variant="outlined" component="label" fullWidth
                    sx={{
                      color: !!errors.product_images ? '#d32f2f' : 'rgb(255 209 2)',
                      borderColor: !!errors.product_images ? '#d32f2f' : 'rgb(255 209 2)',
                      borderRadius: '12px',
                      height: '56px',
                      borderWidth: '1px',
                      '&:hover': {
                        borderWidth: '1px',
                        borderColor: 'rgb(255 209 2)',
                        background: 'rgba(255, 209, 2, 0.1)'
                      }
                    }}>
                    Upload Images and videos
                    <input
                      type="file"
                      accept="image/*,video/*"
                      hidden
                      multiple
                      onChange={handleImageUpload}
                    />
                  </Button>
                  {errors.product_images && (
                    <Typography variant="caption" color="error" sx={{ ml: 2, display: 'block', mt: 0.5 }}>
                      {errors.product_images}
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>
            {form.product_images && form.product_images.length > 0 && (
              <Grid item xs={12}>
                <Box display="flex" flexWrap="wrap" gap={2}>
                    {form.product_images.map((media, idx) => {
                      const isFile = media instanceof File;
                      let previewUrl = isFile ? URL.createObjectURL(media) : media;

                      // Prefix relative paths with base URL if it's an existing image (string)
                      if (!isFile && typeof media === 'string' && !media.startsWith('http') && !media.startsWith('data:')) {
                        // Using the domain from Productmanagement.js as a reference
                        previewUrl = `https://api.glamcompany.in/${media}`;
                      }

                      const isVideo = (isFile && media.type.startsWith('video/')) || (typeof media === 'string' && (media.startsWith('data:video') || /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(media)));

                      return (
                        <Box
                          key={idx}
                          sx={{
                            position: 'relative',
                            display: 'inline-block',
                            width: 100,
                            height: 100,
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            overflow: 'visible',
                            bgcolor: '#f5f5f5'
                          }}
                        >
                          {isVideo ? (
                            <Box
                              component="video"
                              src={previewUrl}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '8px'
                              }}
                              controls
                            />
                          ) : (
                            <Box
                              component="img"
                              src={previewUrl}
                              alt={`media-${idx}`}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '8px'
                              }}
                              onError={(e) => {
                                // Fallback for broken images
                                e.target.src = '/img/default-product.png';
                              }}
                            />
                          )}
                          {formMode === 'edit' && (
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveImage(idx)}
                              sx={{
                                position: 'absolute',
                                top: -10,
                                right: -10,
                                backgroundColor: '#ff4d4f',
                                color: 'white',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                '&:hover': {
                                  backgroundColor: '#ff7875'
                                },
                                width: 20,
                                height: 20,
                                padding: 0,
                                minWidth: 0,
                                zIndex: 1
                              }}
                            >
                              <span style={{ fontSize: '14px', lineHeight: 1 }}>×</span>
                            </IconButton>
                          )}
                        </Box>
                      );
                    })}

                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={3}>
              {formMode === 'view' ? (
                renderViewField('Unit Type', form.unitType || 'Not selected')
              ) : (
                <FormControl fullWidth required error={!!errors.unitType}>
                  <InputLabel>Unit Type</InputLabel>
                  <Select
                    label="Unit Type"
                    name="unitType"
                    value={form.unitType}
                    onChange={onFormChange}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value=""><em>Select</em></MenuItem>
                    <MenuItem value="unit">unit</MenuItem>
                    <MenuItem value="weight">weight</MenuItem>
                  </Select>
                  {errors.unitType && <Typography variant="caption" color="error" sx={{ ml: 2 }}>{errors.unitType}</Typography>}
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} sm={3}>
              {formMode === 'view' ? (
                renderViewField('Unit Value', form.unitValue)
              ) : (
                <TextField
                  fullWidth
                  label="Unit Value"
                  name="unitValue"
                  type="number"
                  value={form.unitValue}
                  onChange={onFormChange}
                  variant="outlined"
                  required
                  error={!!errors.unitValue}
                  helperText={errors.unitValue}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              )}
            </Grid>

            <Grid item xs={12} sm={3}>
              {formMode === 'view' ? (
                renderViewField('Unit Name', form.unitName || 'Not selected')
              ) : (
                <FormControl fullWidth error={!!errors.unitName} required={form.unitType === 'weight'}>
                  <InputLabel>Unit Name</InputLabel>
                  <Select
                    label="Unit Name"
                    name="unitName"
                    value={form.unitName}
                    onChange={onFormChange}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value=""><em>Select</em></MenuItem>
                    <MenuItem value="ml">ml</MenuItem>
                    <MenuItem value="L">L</MenuItem>
                    <MenuItem value="g">g</MenuItem>
                    <MenuItem value="kg">kg</MenuItem>
                    <MenuItem value="pouch">pouch</MenuItem>
                    <MenuItem value="packet">packet</MenuItem>
                    <MenuItem value="sachet">sachet</MenuItem>
                    <MenuItem value="unit">unit</MenuItem>
                  </Select>
                  {errors.unitName && <Typography variant="caption" color="error" sx={{ ml: 2 }}>{errors.unitName}</Typography>}
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} sm={3}>
              {formMode === 'view' ? (
                renderViewField('Minimum Stock Quantity', form.minimumStockQuantity)
              ) : (
                <TextField
                  fullWidth
                  label="Minimum Stock Quantity"
                  name="minimumStockQuantity"
                  type="number"
                  value={form.minimumStockQuantity || ''}
                  onChange={onFormChange}
                  variant="outlined"
                />
              )}
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={3}>
            {/* Free Product Toggle */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!form.isFree}
                    onChange={(e) => onFormChange({
                      target: { name: 'isFree', value: e.target.checked }
                    })}
                  />
                }
                label="Is Free Product"
              />
            </Grid>

            {/* If Free, show threshold and auto add, else show B2B/B2C */}
            {!form.isFree && (
              <>
                {/* GENERAL Price Details */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>General Price Details</Typography>
                </Grid>
                {/* Common MRP Field */}
                <Grid item xs={12} sm={6}>
                  {formMode === 'view' ? (
                    renderViewField('MRP', form.b2c_mrp ? `₹${form.b2c_mrp}` : 'Not set')
                  ) : (
                    <TextField
                      fullWidth
                      label="MRP"
                      name="b2c_mrp"
                      type="number"
                      value={form.b2c_mrp ?? ''}
                      onChange={onFormChange}
                      inputProps={{ min: 0 }}
                      required
                      error={!!errors.b2c_mrp}
                      helperText={errors.b2c_mrp}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                  )}
                </Grid>
                {/* GST Field */}
                <Grid item xs={12} sm={6}>
                  {formMode === 'view' ? (
                    renderViewField('GST (%)', form.gst ? `${form.gst}%` : 'Not selected')
                  ) : (
                    <FormControl fullWidth required error={!!errors.gst}>
                      <InputLabel>GST (%)</InputLabel>
                      <Select
                        label="GST (%)"
                        name="gst"
                        value={form.gst}
                        onChange={onFormChange}
                        sx={{ borderRadius: '12px' }}
                      >
                        <MenuItem value=""><em>Select</em></MenuItem>
                        <MenuItem value="0">0</MenuItem>
                        <MenuItem value="5">5</MenuItem>
                        <MenuItem value="12">12</MenuItem>
                        <MenuItem value="18">18</MenuItem>
                        <MenuItem value="28">28</MenuItem>
                      </Select>
                      {errors.gst && <Typography variant="caption" color="error" sx={{ ml: 2 }}>{errors.gst}</Typography>}
                    </FormControl>
                  )}
                </Grid>

                {/* B2B Price Details */}
                {(Array.isArray(form.visibility) ? form.visibility.includes('b2b') : String(form.visibility || '').includes('b2b')) && (
                  <>
                    <Grid item xs={12} mt={3}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Business-to-Business (B2B) Price Details</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                  {formMode === 'view' ? (
                    renderViewField('Discount Type (B2B)', form.b2b_discountType || 'Not selected')
                  ) : (
                    <FormControl fullWidth>
                      <InputLabel>Discount Type (B2B)</InputLabel>
                      <Select
                        label="Discount Type (B2B)"
                        name="b2b_discountType"
                        value={form.b2b_discountType || ''}
                        onChange={onFormChange}
                      >
                        <MenuItem value=""><em>Select</em></MenuItem>
                        <MenuItem value="flat">Flat</MenuItem>
                        <MenuItem value="percentage">Percentage</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {formMode === 'view' ? (
                    renderViewField('Discount Value (B2B)', form.b2b_discountValue ? `${form.b2b_discountValue}${form.b2b_discountType === 'percentage' ? '%' : ' ₹'}` : 'Not set')
                  ) : (
                    <TextField
                      fullWidth
                      label="Discount Value (B2B)"
                      name="b2b_discountValue"
                      type="number"
                      value={form.b2b_discountValue ?? ''}
                      onChange={onFormChange}
                      inputProps={{
                        min: 0,
                        max: form.b2b_discountType === 'percentage' ? 100 : undefined
                      }}
                      disabled={!form.b2b_discountType}
                      required={!!form.b2b_discountType}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {formMode === 'view' ? (
                    renderViewField('Selling Price (B2B)', form.b2b_sellingPrice ? `₹${form.b2b_sellingPrice}` : 'Not set')
                  ) : (
                    <TextField
                      fullWidth
                      label="Selling Price (B2B)"
                      name="b2b_sellingPrice"
                      type="number"
                      value={form.b2b_sellingPrice ?? calculateB2BSellingPrice() ?? ''}
                      onChange={onFormChange}
                      variant="outlined"
                      required
                      error={!!errors.b2b_sellingPrice}
                      helperText={errors.b2b_sellingPrice}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                  )}
                </Grid>
                </>
                )}

                {/* B2C Price Details */}
                {(Array.isArray(form.visibility) ? form.visibility.includes('b2c') : String(form.visibility || '').includes('b2c')) && (
                  <>
                    <Grid item xs={12} mt={3}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Business-to-Consumer (B2C) Price Details</Typography>
                    </Grid>
                {/* Discount Type */}
                <Grid item xs={12} sm={4}>
                  {formMode === 'view' ? (
                    renderViewField('Discount Type (B2C)', form.b2c_discountType || 'Not selected')
                  ) : (
                    <FormControl fullWidth>
                      <InputLabel>Discount Type (B2C)</InputLabel>
                      <Select
                        label="Discount Type (B2C)"
                        name="b2c_discountType"
                        value={form.b2c_discountType || ''}
                        onChange={onFormChange}
                      >
                        <MenuItem value=""><em>Select</em></MenuItem>
                        <MenuItem value="flat">Flat</MenuItem>
                        <MenuItem value="percentage">Percentage</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Grid>
                {/* Discount Value */}
                <Grid item xs={12} sm={4}>
                  {formMode === 'view' ? (
                    renderViewField('Discount Value (B2C)', form.b2c_discountValue ? `${form.b2c_discountValue}${form.b2c_discountType === 'percentage' ? '%' : ' ₹'}` : 'Not set')
                  ) : (
                    <TextField
                      fullWidth
                      label="Discount Value (B2C)"
                      name="b2c_discountValue"
                      type="number"
                      value={form.b2c_discountValue ?? ''}
                      onChange={onFormChange}
                      inputProps={{
                        min: 0,
                        max: form.b2c_discountType === 'percentage' ? 100 : undefined
                      }}
                      disabled={!form.b2c_discountType}
                      required={!!form.b2c_discountType}
                    />
                  )}
                </Grid>
                {/* Auto-calculated Selling Price */}
                <Grid item xs={12} sm={4}>
                  {formMode === 'view' ? (
                    renderViewField('Selling Price (B2C)', form.b2c_sellingPrice ? `₹${form.b2c_sellingPrice}` : 'Not set')
                  ) : (
                    <TextField
                      fullWidth
                      label="Selling Price (B2C)"
                  name="b2c_sellingPrice"
                      type="number"
                      value={form.b2c_sellingPrice ?? calculateSellingPrice() ?? ''}
                      onChange={onFormChange}
                      variant="outlined"
                      required
                      error={!!errors.b2c_sellingPrice}
                      helperText={errors.b2c_sellingPrice}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                  )}
                </Grid>
                </>
                )}
              </>
            )}

            {form.isFree && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Free Product Settings</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {formMode === 'view' ? (
                    renderViewField('Threshold Amount', form.thresholdAmount ? `₹${form.thresholdAmount}` : 'Not set')
                  ) : (
                    <TextField
                      fullWidth
                      label="Threshold Amount"
                      name="thresholdAmount"
                      type="number"
                      value={form.thresholdAmount || ''}
                      onChange={onFormChange}
                      variant="outlined"
                      required={!!form.isFree}
                      error={!!errors.thresholdAmount}
                      helperText={errors.thresholdAmount}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {formMode === 'view' ? (
                    renderViewField('Auto Add', form.autoAdd ? 'Yes' : 'No')
                  ) : (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!form.autoAdd}
                          onChange={(e) => onFormChange({
                            target: { name: 'autoAdd', value: e.target.checked }
                          })}
                        />
                      }
                      label="Auto Add to Cart"
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {formMode === 'view' ? (
                    renderViewField('GST (%)', form.gst ? `${form.gst}%` : 'Not selected')
                  ) : (
                    <FormControl fullWidth required>
                      <InputLabel>GST (%)</InputLabel>
                      <Select
                        label="GST (%)"
                        name="gst"
                        value={form.gst}
                        onChange={onFormChange}
                      >
                        <MenuItem value=""><em>Select</em></MenuItem>
                        <MenuItem value="0">0</MenuItem>
                        <MenuItem value="5">5</MenuItem>
                        <MenuItem value="18">18</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Grid>
              </>
            )}
          </Grid>
        );
      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {formMode === 'view' ? (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>Dimensions</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      {renderViewField('Length', form.length ? `${form.length}` : 'Not provided')}
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      {renderViewField('Width', form.width ? `${form.width}` : 'Not provided')}
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      {renderViewField('Height', form.height ? `${form.height}` : 'Not provided')}
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      {renderViewField('Dimension Unit', form.dimensionUnit || 'Not selected')}
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      {renderViewField('Weight', form.weight ? `${form.weight}` : 'Not provided')}
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      {renderViewField('Weight Unit', form.weightUnit || 'Not selected')}
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>Product Dimensions</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth>
                        <InputLabel>Dimension Unit</InputLabel>
                        <Select
                          label="Dimension Unit"
                          name="dimensionUnit"
                          value={form.dimensionUnit || ''}
                          onChange={onFormChange}
                        >
                          <MenuItem value=""><em>Select</em></MenuItem>
                          <MenuItem value="cm">Centimeters (cm)</MenuItem>
                          <MenuItem value="mm">Millimeters (mm)</MenuItem>
                          <MenuItem value="in">Inches (in)</MenuItem>
                          <MenuItem value="m">Meters (m)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Length"
                        name="length"
                        type="number"
                        value={form.length || ''}
                        onChange={onFormChange}
                        variant="outlined"
                        inputProps={{ min: 0, step: "0.01" }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Width"
                        name="width"
                        type="number"
                        value={form.width || ''}
                        onChange={onFormChange}
                        variant="outlined"
                        inputProps={{ min: 0, step: "0.01" }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Height"
                        name="height"
                        type="number"
                        value={form.height || ''}
                        onChange={onFormChange}
                        variant="outlined"
                        inputProps={{ min: 0, step: "0.01" }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth>
                        <InputLabel>Weight Unit</InputLabel>
                        <Select
                          label="Weight Unit"
                          name="weightUnit"
                          value={form.weightUnit || ''}
                          onChange={onFormChange}
                        >
                          <MenuItem value=""><em>Select</em></MenuItem>
                          <MenuItem value="g">Grams (g)</MenuItem>
                          <MenuItem value="kg">Kilograms (kg)</MenuItem>
                          <MenuItem value="mg">Milligrams (mg)</MenuItem>
                          <MenuItem value="lb">Pounds (lb)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Weight"
                        name="weight"
                        type="number"
                        value={form.weight || ''}
                        onChange={onFormChange}
                        variant="outlined"
                        inputProps={{ min: 0, step: "0.01" }}
                      />
                    </Grid>

                  </Grid>
                </Box>
              )}
            </Grid>
          </Grid>
        );
      case 4:
        return (
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('Status', form.status || 'Not selected')
              ) : (
                <FormControl fullWidth required error={!!errors.status}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    name="status"
                    value={form.status}
                    onChange={onFormChange}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value=""><em>Select</em></MenuItem>
                    <MenuItem value="approved">approved</MenuItem>
                    <MenuItem value="pending">pending</MenuItem>
                    <MenuItem value="rejected">rejected</MenuItem>
                  </Select>
                  {errors.status && <Typography variant="caption" color="error" sx={{ ml: 2 }}>{errors.status}</Typography>}
                </FormControl>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('Ideal For', form.ideal_for || 'Not selected')
              ) : (
                <FormControl fullWidth required error={!!errors.ideal_for}>
                  <InputLabel>Ideal For</InputLabel>
                  <Select
                    label="Ideal For"
                    name="ideal_for"
                    value={form.ideal_for}
                    onChange={onFormChange}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value=""><em>Select</em></MenuItem>
                    <MenuItem value="men">men</MenuItem>
                    <MenuItem value="women">women</MenuItem>
                    <MenuItem value="unisex">unisex</MenuItem>
                  </Select>
                  {errors.ideal_for && <Typography variant="caption" color="error" sx={{ ml: 2 }}>{errors.ideal_for}</Typography>}
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('Applied For', form.applied_for)
              ) : (
                <TextField
                  fullWidth
                  label="Applied For"
                  name="applied_for"
                  value={form.applied_for}
                  onChange={onFormChange}
                  variant="outlined"
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('Container Type', form.container_type || 'Not selected')
              ) : (
                <FormControl fullWidth required error={!!errors.container_type}>
                  <InputLabel>Container Type</InputLabel>
                  <Select
                    label="Container Type"
                    name="container_type"
                    value={form.container_type}
                    onChange={onFormChange}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value=""><em>Select</em></MenuItem>
                    <MenuItem value="bottle">bottle</MenuItem>
                    <MenuItem value="box">box</MenuItem>
                    <MenuItem value="packet">packet</MenuItem>
                    <MenuItem value="can">can</MenuItem>
                    <MenuItem value="jar">jar</MenuItem>
                    <MenuItem value="bag">bag</MenuItem>
                    <MenuItem value="pouch">pouch</MenuItem>
                    <MenuItem value="tube">tube</MenuItem>
                    <MenuItem value="roll">roll</MenuItem>
                    <MenuItem value="crate">crate</MenuItem>
                    <MenuItem value="sachet">sachet</MenuItem>
                    <MenuItem value="drum">drum</MenuItem>
                    <MenuItem value="blister_pack">blister_pack</MenuItem>
                    <MenuItem value="strip">strip</MenuItem>
                    <MenuItem value="unit">unit</MenuItem>
                  </Select>
                  {errors.container_type && <Typography variant="caption" color="error" sx={{ ml: 2 }}>{errors.container_type}</Typography>}
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('Country of Origin', form.country_of_orgin)
              ) : (
                <TextField
                  fullWidth
                  label="Country of Origin"
                  name="country_of_orgin"
                  value={form.country_of_orgin}
                  onChange={onFormChange}
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('Color', form.color)
              ) : (
                <TextField
                  fullWidth
                  label="Color"
                  name="color"
                  value={form.color}
                  onChange={onFormChange}
                  variant="outlined"
                />
              )}
            </Grid>
            {/* Tags - multi entry */}
            <Grid item xs={12}>
              {formMode === 'view' ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(Array.isArray(form.tags) ? form.tags : []).length > 0 ? (
                      (form.tags || []).map((t, idx) => (
                        <Chip key={idx} label={t} size="small" />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">No tags</Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={Array.isArray(form.tags) ? form.tags : []}
                  onChange={(event, newValue) => {
                    onFormChange({ target: { name: 'tags', value: newValue } });
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return <Chip key={key} variant="outlined" label={option} {...tagProps} />;
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Search keywords"
                      placeholder="Type and press Enter"
                      fullWidth
                    />
                  )}
                />
              )}
            </Grid>


            {/* SEO & Marketing */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}><Chip label="SEO & Marketing" /></Divider>
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('Slug', form.slug)
              ) : (
                <TextField
                  fullWidth
                  label="Slug (URL-friendly)"
                  name="slug"
                  value={form.slug || ''}
                  onChange={onFormChange}
                  placeholder="e.g. rose-shampoo-luxury"
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('Meta Title', form.meta_title)
              ) : (
                <TextField
                  fullWidth
                  label="Meta Title"
                  name="meta_title"
                  value={form.meta_title || ''}
                  onChange={onFormChange}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              {formMode === 'view' ? (
                renderViewField('Meta Description', form.meta_description)
              ) : (
                <TextField
                  fullWidth
                  label="Meta Description"
                  name="meta_description"
                  value={form.meta_description || ''}
                  onChange={onFormChange}
                  multiline
                  rows={2}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('Badge', form.badge)
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Badge</InputLabel>
                  <Select
                    name="badge"
                    value={form.badge || ''}
                    label="Badge"
                    onChange={onFormChange}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {BADGE_OPTIONS.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('Is Featured', form.is_featured ? 'Yes' : 'No')
              ) : (
                <Box display="flex" alignItems="center" height="100%">
                  <Typography variant="body1" mr={2}>Is Featured</Typography>
                  <input
                    style={{ transform: 'scale(1.5)', cursor: 'pointer' }}
                    type="checkbox"
                    name="is_featured"
                    checked={!!form.is_featured}
                    onChange={e => onFormChange({
                      target: { name: 'is_featured', value: e.target.checked }
                    })}
                  />
                </Box>
              )}
            </Grid>

            {/* Product Specifications */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}><Chip label="Product Specifications" /></Divider>
            </Grid>
            <Grid item xs={12}>
              {formMode === 'view' ? (
                renderViewField('Ingredients', form.ingredients)
              ) : (
                <TextField
                  fullWidth
                  label="Ingredients"
                  name="ingredients"
                  value={form.ingredients || ''}
                  onChange={onFormChange}
                  multiline
                  rows={3}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {formMode === 'view' ? (
                renderViewField('Skin Type', form.skin_type)
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Skin Type</InputLabel>
                  <Select
                    name="skin_type"
                    value={form.skin_type || ''}
                    label="Skin Type"
                    onChange={onFormChange}
                  >
                    <MenuItem value=""><em>Not Specified</em></MenuItem>
                    {SKIN_TYPES.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {formMode === 'view' ? (
                renderViewField('Hair Type', form.hair_type)
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Hair Type</InputLabel>
                  <Select
                    name="hair_type"
                    value={form.hair_type || ''}
                    label="Hair Type"
                    onChange={onFormChange}
                  >
                    <MenuItem value=""><em>Not Specified</em></MenuItem>
                    {HAIR_TYPES.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {formMode === 'view' ? (
                renderViewField('Fragrance Notes', form.fragrance_notes)
              ) : (
                <TextField
                  fullWidth
                  label="Fragrance Notes"
                  name="fragrance_notes"
                  value={form.fragrance_notes || ''}
                  onChange={onFormChange}
                />
              )}
            </Grid>

            {/* Trust Badges */}
            <Grid item xs={12} sm={4}>
              {formMode === 'view' ? (
                renderViewField('Is Vegan', form.is_vegan ? 'Yes' : 'No')
              ) : (
                <Box display="flex" alignItems="center" height="100%">
                  <Typography variant="body1" mr={2}>Is Vegan</Typography>
                  <input
                    style={{ transform: 'scale(1.5)', cursor: 'pointer' }}
                    type="checkbox"
                    name="is_vegan"
                    checked={!!form.is_vegan}
                    onChange={e => onFormChange({
                      target: { name: 'is_vegan', value: e.target.checked }
                    })}
                  />
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {formMode === 'view' ? (
                renderViewField('Cruelty Free', form.cruelty_free ? 'Yes' : 'No')
              ) : (
                <Box display="flex" alignItems="center" height="100%">
                  <Typography variant="body1" mr={2}>Cruelty Free</Typography>
                  <input
                    style={{ transform: 'scale(1.5)', cursor: 'pointer' }}
                    type="checkbox"
                    name="cruelty_free"
                    checked={!!form.cruelty_free}
                    onChange={e => onFormChange({
                      target: { name: 'cruelty_free', value: e.target.checked }
                    })}
                  />
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {formMode === 'view' ? (
                renderViewField('Organic', form.organic ? 'Yes' : 'No')
              ) : (
                <Box display="flex" alignItems="center" height="100%">
                  <Typography variant="body1" mr={2}>Organic</Typography>
                  <input
                    style={{ transform: 'scale(1.5)', cursor: 'pointer' }}
                    type="checkbox"
                    name="organic"
                    checked={!!form.organic}
                    onChange={e => onFormChange({
                      target: { name: 'organic', value: e.target.checked }
                    })}
                  />
                </Box>
              )}
            </Grid>

            {/* Logistics & Policy */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}><Chip label="Logistics & Policy" /></Divider>
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('Shipping Class', form.shipping_class)
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Shipping Class</InputLabel>
                  <Select
                    name="shipping_class"
                    value={form.shipping_class || ''}
                    label="Shipping Class"
                    onChange={onFormChange}
                  >
                    <MenuItem value=""><em>Default Shipping</em></MenuItem>
                    {SHIPPING_CLASSES.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {formMode === 'view' ? (
                renderViewField('Return Policy (Days)', form.return_policy ? `${form.return_policy} Days` : 'No Return')
              ) : (
                <TextField
                  fullWidth
                  label="Return Policy (Days)"
                  name="return_policy"
                  type="number"
                  value={form.return_policy || ''}
                  onChange={onFormChange}
                  placeholder="Enter number of days (0 for No Return)"
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              )}
            </Grid>
            {/* <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Created Date"
                                name="createdBy"
                                type="date"
                                value={form.createdBy}
                                onChange={onFormChange}
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid> */}
          </Grid>
        );
      case 5:
        return (
          <Box sx={{
            p: { xs: 1, sm: 1.5 },
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}>
            {/* Decorative Elements */}
            <Box sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%)',
              opacity: 0.1,

            }} />

            <Box sx={{
              position: 'absolute',
              bottom: -30,
              left: -30,
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 50%)',
              opacity: 0.1
            }} />

            {/* Header */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 4,
              position: 'relative',
              zIndex: 2
            }}>
              <Box>
                <Typography variant="h4" sx={{
                  fontWeight: 700,
                  background: 'black',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}>
                  Product Review
                </Typography>
              </Box>

              {/* <Chip
                          icon={form.isPublished ? <MdVisibility size={18} /> : <MdVisibilityOff size={18} />}
                          label={form.isPublished ? 'Published' : 'Draft'}
                          color={form.isPublished ? 'success' : 'warning'}
                          sx={{
                            fontWeight: 600,
                            px: 2,
                            py: 1,
                            height: 40,
                            fontSize: '0.875rem',
                            boxShadow: 2
                          }}
                        /> */}
            </Box>

            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              width: '100%',
              maxWidth: '100%'
            }}>
              {/* Top Section - Product Gallery with Left-Right Layout */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                maxWidth: '100%'
              }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 4,
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    overflow: 'hidden',
                    maxWidth: { xs: '100%', sm: '100%', md: 1100 },
                    width: '100%'
                  }}
                >


                  {form.product_images?.length > 0 ? (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', lg: 'row' },
                      gap: { xs: 3, lg: 4 },
                      alignItems: 'flex-start'
                    }}>
                      {/* Left Side - Main Image */}
                      <Box sx={{
                        flex: { xs: '1 1 100%', lg: '0 0 40%' },
                        maxWidth: { xs: '100%', lg: '40%' },
                        width: '100%'
                      }}>
                        <Paper
                          elevation={0}
                          sx={{
                            position: 'relative',
                            borderRadius: 3,
                            overflow: 'hidden',
                            aspectRatio: '1/1',
                            bgcolor: 'grey.50',
                            border: '1px solid',
                            borderColor: 'divider',
                            width: '100%',
                            maxWidth: { xs: '100%', sm: '500px', lg: '450px' },
                            margin: { xs: '0 auto', lg: '0' }
                          }}
                        >
                          {(() => {
                            const media = form.product_images[activeImageIndex];
                            const isVideo = typeof media === 'string' && (media.startsWith('data:video') || /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(media));
                            return isVideo ? (
                              <video
                                src={media}
                                controls
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                  backgroundColor: '#000'
                                }}
                              />
                            ) : (
                              <Box
                                component="img"
                                src={media}
                                alt={`Product View ${activeImageIndex + 1}`}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  transition: 'transform 0.3s ease',
                                  '&:hover': {
                                    transform: 'scale(1.05)'
                                  }
                                }}
                              />
                            );
                          })()}
                          <Chip
                            label={`${activeImageIndex + 1} / ${form.product_images.length}`}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 16,
                              right: 16,
                              bgcolor: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              fontWeight: 600
                            }}
                          />

                          {/* Navigation Arrows */}
                          <IconButton
                            onClick={() => setActiveImageIndex(prev => prev > 0 ? prev - 1 : form.product_images.length - 1)}
                            sx={{
                              position: 'absolute',
                              left: 8,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              bgcolor: 'rgba(255,255,255,0.9)',
                              '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
                            }}
                          >
                            <MdChevronLeft size={20} />
                          </IconButton>
                          <IconButton
                            onClick={() => setActiveImageIndex(prev => prev < form.product_images.length - 1 ? prev + 1 : 0)}
                            sx={{
                              position: 'absolute',
                              right: 8,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              bgcolor: 'rgba(255,255,255,0.9)',
                              '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
                            }}
                          >
                            <MdChevronRight size={20} />
                          </IconButton>
                        </Paper>
                      </Box>

                      {/* Right Side - Product Title, Price & Thumbnails */}
                      <Box sx={{
                        flex: { xs: '1 1 100%', lg: '0 0 58%' },
                        maxWidth: { xs: '100%', lg: '58%' },
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3
                      }}>
                        {/* Product Title & Price */}

                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            background: 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }}
                        >
                          {/* Title and Price in a row */}
                          <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'flex-start' },
                            gap: { xs: 2, sm: 0 },
                            mb: 2
                          }}>
                            <Typography variant="h5" sx={{
                              fontWeight: 700,
                              color: 'text.primary',
                              fontSize: { xs: '1.25rem', sm: '1.5rem' }
                            }}>
                              {form.title || 'Product Title Not Provided'}
                            </Typography>

                            {/* Price section */}
                            <Box sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: { xs: 'flex-start', sm: 'flex-end' }
                            }}>


                            </Box>
                          </Box>

                          {/* Category and subCategory chips */}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 0 }}>
                            <Chip
                              icon={<MdCategory size={16} />}
                              label={categories.find(cat => cat._id === form.category_id)?.title || 'No Category'}
                              variant="outlined"
                              color="primary"
                              sx={{ fontWeight: 500 }}
                            />
                            <Chip
                              icon={<MdCategory size={16} />}
                              label={subcategories.find(sub => sub._id === form.subcategory_id)?.title || 'No Subcategory'}
                              variant="outlined"
                              color="secondary"
                              sx={{ fontWeight: 500 }}
                            />
                          </Box>
                        </Paper>

                        {/* Thumbnail Strip */}
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            background: 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                            Product Images ({form.product_images.length})
                          </Typography>
                          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            {/* <IconButton 
                                                        // onClick={scrollLeft}
                                                        sx={{ 
                                                            position: 'absolute', 
                                                            left: -10, 
                                                            zIndex: 2,
                                                            bgcolor: 'rgba(255,255,255,0.8)',
                                                            '&:hover': { bgcolor: 'white' },
                                                            boxShadow: 1
                                                        }}
                                                        size="small"
                                                    >
                                                        <MdChevronLeft />
                                                    </IconButton> */}
                            <Box
                              // ref={scrollContainerRef}
                              sx={{
                                display: 'flex',
                                gap: 1,
                                overflowX: 'auto',
                                pb: 1,
                                px: 3, // padding for buttons
                                scrollbarWidth: 'none', // hide scrollbar for cleaner look
                                '&::-webkit-scrollbar': { display: 'none' },
                                scrollBehavior: 'smooth'
                              }}
                            >
                              {form.product_images.map((img, idx) => (
                                <Paper
                                  key={idx}
                                  elevation={0}
                                  onClick={() => setActiveImageIndex(idx)}
                                  sx={{
                                    width: { xs: 80, sm: 100, md: 120 },
                                    height: { xs: 80, sm: 100, md: 120 },
                                    flexShrink: 0,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    border: 2,
                                    borderColor: activeImageIndex === idx ? 'primary.main' : 'transparent',
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  {(() => {
                                    const isVideo = typeof img === 'string' && (img.startsWith('data:video') || /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(img));
                                    return isVideo ? (
                                      <video
                                        src={img}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover'
                                        }}
                                      />
                                    ) : (
                                      <Box
                                        component="img"
                                        src={img}
                                        alt={`Thumbnail ${idx + 1}`}
                                        sx={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover'
                                        }}
                                      />
                                    );
                                  })()}
                                </Paper>
                              ))}
                            </Box>
                            {/* <IconButton 
                                                        // onClick={scrollRight}
                                                        sx={{ 
                                                            position: 'absolute', 
                                                            right: -10, 
                                                            zIndex: 2,
                                                            bgcolor: 'rgba(255,255,255,0.8)',
                                                            '&:hover': { bgcolor: 'white' },
                                                            boxShadow: 1
                                                        }}
                                                        size="small"
                                                    >
                                                        <MdChevronRight />
                                                    </IconButton> */}
                          </Box>
                        </Paper>
                      </Box>
                    </Box>
                  ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        aspectRatio: '1/1',
                        borderRadius: 3,
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px dashed',
                        borderColor: 'divider'
                      }}
                    >
                      <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        <MdImage size={48} style={{ marginBottom: 8, opacity: 0.5 }} />
                        <Typography variant="body2">No images uploaded</Typography>
                      </Box>
                    </Paper>
                  )}
                </Paper>
              </Box>

              {/* Bottom Section - Product Details */}
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                width: '100%',
                maxWidth: '100%'
              }}>


                {/* Information Cards */}
                <Grid container spacing={3}>
                  {/* Basic Information */}
                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 4,
                        borderRadius: 4,
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <Avatar sx={{ bgcolor: 'info.main', width: 32, height: 32 }}>
                          <MdInfo size={18} color="white" />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Basic Information
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3 }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            background: alpha(theme.palette.primary.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            // transition: 'transform 0.2s ease',
                            // '&:hover': { transform: 'translateY(-2px)' },
                            height: 120,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48, flexShrink: 0 }}>
                              <MdLocalOffer size={24} color="white" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                UPC Code
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word', lineHeight: 1.2 }}>
                                {form.upc || 'Not provided'}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>

                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            background: alpha(theme.palette.success.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                            // transition: 'transform 0.2s ease',
                            // '&:hover': { transform: 'translateY(-2px)' },
                            height: 120,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Avatar sx={{ bgcolor: 'success.main', width: 48, height: 48, flexShrink: 0 }}>
                              <MdScale size={24} color="white" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                Unit
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word', lineHeight: 1.2 }}>
                                {form.unitValue} {form.unitName}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>

                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            background: alpha(theme.palette.secondary.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                            // transition: 'transform 0.2s ease',
                            // '&:hover': { transform: 'translateY(-2px)' },
                            height: 120,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48, flexShrink: 0 }}>
                              <MdLocalOffer size={24} color="white" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                HSN Code
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word', lineHeight: 1.2 }}>
                                {form.hsn_code || 'No HSN Code'}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>

                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            background: alpha(theme.palette.warning.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                            // transition: 'transform 0.2s ease',
                            // '&:hover': { transform: 'translateY(-2px)' },
                            height: 120,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Avatar sx={{ bgcolor: 'warning.main', width: 48, height: 48, flexShrink: 0 }}>
                              <MdSchedule size={24} color="white" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                Status
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word', lineHeight: 1.2 }}>
                                {form.status || 'Not provided'}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Price Information */}
                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 4,
                        borderRadius: 4,
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <Avatar sx={{ bgcolor: 'success.main', width: 32, height: 32 }}>
                          <MdCurrencyRupee size={18} color="white" />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Price Information
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3 }}>
                        {/* If not free, show B2B and B2C cards */}
                        {!form.isFree && (
                          <>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 3,
                                borderRadius: 3,
                                background: alpha(theme.palette.primary.main, 0.08),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                // transition: 'transform 0.2s ease',
                                // '&:hover': { transform: 'translateY(-2px)' }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                                  <MdShoppingBag size={20} color="white" />
                                </Avatar>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                                    B2B Price
                                  </Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                    ₹{form.b2b_sellingPrice || 'Not set'}
                                  </Typography>
                                </Box>
                              </Box>
                              {form.b2b_discountType && form.b2b_discountValue && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip
                                    label={`${form.b2b_discountValue}${form.b2b_discountType === 'percentage' ? '% OFF' : ' ₹ OFF'}`}
                                    color="primary"
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </Box>
                              )}
                            </Paper>

                            {/* B2C Price */}
                            <Paper
                              elevation={0}
                              sx={{
                                p: 3,
                                borderRadius: 3,
                                background: alpha(theme.palette.success.main, 0.08),
                                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                // transition: 'transform 0.2s ease',
                                // '&:hover': { transform: 'translateY(-2px)' }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                                  <MdCurrencyRupee size={20} color="white" />
                                </Avatar>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                                    Selling Price (B2C)
                                  </Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                                    ₹{form.b2c_sellingPrice || 'Not set'}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ mt: -1, mb: 1 }}>
                                {form.b2c_mrp && (
                                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    MRP: ₹{form.b2c_mrp}
                                  </Typography>
                                )}
                              </Box>
                              {form.b2c_discountType && form.b2c_discountValue && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip
                                    label={`${form.b2c_discountValue}${form.b2c_discountType === 'percentage' ? '% OFF' : ' ₹ OFF'}`}
                                    color="success"
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </Box>
                              )}
                            </Paper>
                          </>
                        )}

                        {/* If free, show Threshold and Auto Add */}
                        {form.isFree && (
                          <>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 3,
                                borderRadius: 3,
                                background: alpha(theme.palette.info.main, 0.08),
                                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                                  <MdCurrencyRupee size={20} color="white" />
                                </Avatar>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                                    Threshold Amount
                                  </Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main' }}>
                                    {form.thresholdAmount ? `₹${form.thresholdAmount}` : 'Not set'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Paper>

                            <Paper
                              elevation={0}
                              sx={{
                                p: 3,
                                borderRadius: 3,
                                background: alpha(theme.palette.warning.main, 0.08),
                                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
                                  <MdShoppingBag size={20} color="white" />
                                </Avatar>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                                    Auto Add to Cart
                                  </Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main' }}>
                                    {form.autoAdd ? 'Yes' : 'No'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Paper>
                          </>
                        )}
                      </Box>
                    </Paper>
                  </Grid>



                  {/* Product Description */}
                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 4,
                        borderRadius: 4,
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <Avatar sx={{ bgcolor: 'info.main', width: 32, height: 32 }}>
                          <MdInfo size={18} color="white" />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Product Description
                        </Typography>
                      </Box>

                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          background: alpha(theme.palette.info.main, 0.08),
                          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                        }}
                      >
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          {form.description || 'No description provided'}
                        </Typography>
                      </Paper>
                    </Paper>
                  </Grid>

                  {/* Product Details */}
                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 4,
                        borderRadius: 4,
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                          <MdCategory size={18} color="white" />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Product Details
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            background: alpha(theme.palette.secondary.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                            height: 120,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48, flexShrink: 0 }}>
                              <MdLocalOffer size={24} color="white" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                Brand
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word', lineHeight: 1.2 }}>
                                {form.brand || 'Not provided'}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>

                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            background: alpha(theme.palette.warning.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                            height: 120,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Avatar sx={{ bgcolor: 'warning.main', width: 48, height: 48, flexShrink: 0 }}>
                              <MdStar size={24} color="white" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                Ideal For
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word', lineHeight: 1.2 }}>
                                {form.ideal_for || 'Not provided'}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>

                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            background: alpha(theme.palette.error.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                            height: 120,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Avatar sx={{ bgcolor: 'error.main', width: 48, height: 48, flexShrink: 0 }}>
                              <MdSchedule size={24} color="white" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                Applied For
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word', lineHeight: 1.2 }}>
                                {form.applied_for || 'Not provided'}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>

                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            background: alpha(theme.palette.info.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                            height: 120,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Avatar sx={{ bgcolor: 'info.main', width: 48, height: 48, flexShrink: 0 }}>
                              <MdScale size={24} color="white" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                Container Type
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word', lineHeight: 1.2 }}>
                                {form.container_type || 'Not provided'}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>

                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            background: alpha(theme.palette.success.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                            height: 120,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Avatar sx={{ bgcolor: 'success.main', width: 48, height: 48, flexShrink: 0 }}>
                              <MdCategory size={24} color="white" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                Country of Origin
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word', lineHeight: 1.2 }}>
                                {form.country_of_orgin || 'Not provided'}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>

                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            background: alpha(theme.palette.primary.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            height: 120,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48, flexShrink: 0 }}>
                              <MdLocalOffer size={24} color="white" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                Color
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word', lineHeight: 1.2 }}>
                                {form.color || 'Not provided'}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Box>

                      {/* Tags Section */}
                      <Box sx={{ mt: 3, px: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <Avatar sx={{ bgcolor: 'info.main', width: 28, height: 28 }}>
                            <MdLocalOffer size={16} color="white" />
                          </Avatar>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Tags
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {((Array.isArray(form.tags) ? form.tags : []) || []).length > 0 ? (
                            (form.tags || []).map((t, idx) => (
                              <Chip key={idx} label={t} size="small" variant="outlined" sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.info.main, 0.05) }} />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">No tags provided</Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>



                  {/* Key Features */}
                  {form.key_features && (
                    <Grid item xs={12}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 4,
                          borderRadius: 4,
                          background: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                            <MdStar size={18} color="white" />
                          </Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Key Features
                          </Typography>
                        </Box>

                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            background: alpha(theme.palette.primary.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                          }}
                        >
                          <div
                            dangerouslySetInnerHTML={{ __html: form.key_features }}
                            style={{
                              lineHeight: 1.6,
                              '& ul, & ol': { paddingLeft: '20px' },
                              '& li': { marginBottom: '8px' }
                            }}
                          />
                        </Paper>
                      </Paper>
                    </Grid>
                  )}

                  {/* How to Use */}
                  {form.how_to_use && (
                    <Grid item xs={12}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 4,
                          borderRadius: 4,
                          background: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                          <Avatar sx={{ bgcolor: 'success.main', width: 32, height: 32 }}>
                            <MdInfo size={18} color="white" />
                          </Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            How to Use
                          </Typography>
                        </Box>

                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            background: alpha(theme.palette.success.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                          }}
                        >
                          <div
                            dangerouslySetInnerHTML={{ __html: form.how_to_use }}
                            style={{
                              lineHeight: 1.6,
                              '& ul, & ol': { paddingLeft: '20px' },
                              '& li': { marginBottom: '8px' }
                            }}
                          />
                        </Paper>
                      </Paper>
                    </Grid>
                  )}
                  {/* Additional Information */}
                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 4,
                        borderRadius: 4,
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                          <MdSettings size={18} color="white" />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Additional Information
                        </Typography>
                      </Box>

                      <Grid container spacing={3}>
                        {/* SEO & Marketing */}
                        <Grid item xs={12}>
                           <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, mb: 1 }}>SEO & Marketing</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">Slug</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.slug || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">Meta Title</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.meta_title || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">Badge</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.badge || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">Meta Description</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.meta_description || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">Is Featured</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.is_featured ? 'Yes' : 'No'}</Typography>
                        </Grid>

                        {/* Product Specifications */}
                        <Grid item xs={12}>
                           <Divider sx={{ my: 1 }} />
                           <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, mb: 1 }}>Specifications</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">Ingredients</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.ingredients || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">Skin Type</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.skin_type || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">Hair Type</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.hair_type || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">Fragrance Notes</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.fragrance_notes || 'N/A'}</Typography>
                        </Grid>

                        {/* Trust Badges */}
                        <Grid item xs={12}>
                           <Divider sx={{ my: 1 }} />
                           <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, mb: 1 }}>Trust Badges & Policy</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">Is Vegan</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.is_vegan ? 'Yes' : 'No'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">Cruelty Free</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.cruelty_free ? 'Yes' : 'No'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">Organic</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.organic ? 'Yes' : 'No'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">Shipping Class</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.shipping_class || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">Return Policy (Days)</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.return_policy ? `${form.return_policy} Days` : 'No Return'}</Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                </Grid>

                {/* Quick Stats */}
                <Grid container spacing={3}>
                  <Grid item xs={6} sm={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        textAlign: 'center',
                        // transition: 'transform 0.2s ease',
                        // '&:hover': { transform: 'translateY(-4px)' }
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        {form.product_images?.length || 0}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Images
                      </Typography>
                    </Paper>
                  </Grid>



                  <Grid item xs={6} sm={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                        color: 'white',
                        textAlign: 'center',
                        // transition: 'transform 0.2s ease',
                        // '&:hover': { transform: 'translateY(-4px)' }
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        {form.b2c_discountValue || 0}{form.b2c_discountType === 'percentage' ? '%' : '₹'}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Discount
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                        color: 'white',
                        textAlign: 'center',
                        // transition: 'transform 0.2s ease',
                        // '&:hover': { transform: 'translateY(-4px)' }
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        <MdScale size={25} color="white" />{form.minimumStockQuantity || 'Not set'}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Minimum Stock Quantity
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ fontFamily: 'Mulish, sans-serif' }}>
      {/* Add Category Modal */}
      <Dialog
        open={addOpen}
        onClose={handleAddCategoryClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            py: 2,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Add New Category
          <IconButton
            onClick={handleAddCategoryClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)'
              }
            }}
          >
            <FiX size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 3, px: 3 }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: '10PX' }}>
            {/* Title Field */}
            <TextField
              variant="outlined"
              label="Category Name"
              value={newCategory.title}
              onChange={e => setNewCategory({ ...newCategory, title: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FiType color="#6c757d" size={18} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: '8px',
                  '& input': {
                    py: 1.5
                  }
                }
              }}
            />

            {/* Image Upload */}
            <Box>
              <Typography variant="subtitle2" sx={{
                mb: 1,
                fontWeight: 500,
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <FiUploadCloud size={16} />
                Category Image
              </Typography>

              <Box
                component="label"
                htmlFor="category-image-upload"
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(0, 123, 255, 0.05)'
                  }
                }}
              >
                {imagePreview ? (
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: '100%',
                        maxHeight: 200,
                        objectFit: 'contain',
                        borderRadius: 6
                      }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.7)'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setImagePreview('');
                        setNewCategory({ ...newCategory, image: '' });
                      }}
                    >
                      <FiTrash2 size={16} />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <FiUploadCloud size={48} color="#adb5bd" style={{ marginBottom: '8px' }} />
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <Box component="span" sx={{ color: 'primary.main', fontWeight: 500 }}>
                        Click to upload
                      </Box> or drag and drop
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                      PNG, JPG, GIF (Max 2MB)
                    </Typography>
                  </>
                )}

                <input
                  id="category-image-upload"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file) {
                      setNewCategory({ ...newCategory, image: file });
                      setImagePreview(URL.createObjectURL(file));
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Status Selector */}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={newCategory.status}
                label="Status"
                onChange={e => setNewCategory({ ...newCategory, status: e.target.value })}
                variant="outlined"
                sx={{
                  borderRadius: '8px',
                  '& .MuiSelect-select': {
                    py: 1.5
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      marginTop: '4px'
                    }
                  }
                }}
              >
                <MenuItem value="active">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiCircle size={14} color="#28a745" />
                    Active
                  </Box>
                </MenuItem>
                <MenuItem value="inactive">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiCircle size={14} color="#6c757d" />
                    Inactive
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Category Cluster</InputLabel>
              <Select
                value={newCategory.categoryCluster}
                label="Category Cluster *"
                onChange={e => setNewCategory({ ...newCategory, categoryCluster: e.target.value })}
                variant="outlined"
                sx={{
                  borderRadius: '8px',
                  '& .MuiSelect-select': {
                    py: 1.5
                  }
                }}
              >
                <MenuItem value="" disabled><em>Select Cluster</em></MenuItem>
                {categoryClusters.map((cluster) => (
                    <MenuItem key={cluster._id} value={cluster._id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {cluster.name}
                        </Box>
                    </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          px: 3,
          py: 2,
          gap: 2
        }}>
          <Button
            onClick={handleAddCategoryClose}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              py: 1,
              border: '1px solid grey',
              color: "grey",
              fontWeight: 500,
              '&:hover': {
                borderWidth: '1.5px',
                backgroundColor: '#f8f9fa'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddCategorySubmit}
            disabled={!newCategory.title}
            sx={{
              border: "1px solid black",
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 500,
              boxShadow: 'none',
              background: 'rgb(255 209 2)',
              color: 'black',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                background: 'rgb(255 209 2)'
              },
              '&:disabled': {
                background: '#e9ecef',
                color: '#adb5bd'
              }
            }}
          >
            Create Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Subcategory Dialog */}
      <Dialog
        open={addSubOpen}
        onClose={handleAddSubCategoryClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            py: 2,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FiLayers size={20} />
            Add New Subcategory
          </Box>
          <IconButton
            onClick={handleAddSubCategoryClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)'
              }
            }}
          >
            <FiX size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 3, px: 3 }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {/* Title Field */}
            <TextField
              variant="outlined"
              label="Subcategory Name"
              value={newSubcategory.title}
              onChange={e => setNewSubcategory({ ...newSubcategory, title: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FiType color="#6c757d" size={18} />
                  </InputAdornment>
                ),
                sx: { borderRadius: '8px' }
              }}
            />

            {/* Image Upload */}
            <Box>
              <Typography variant="subtitle2" sx={{
                mb: 1,
                fontWeight: 500,
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <FiUploadCloud size={16} />
                Subcategory Image
              </Typography>

              <Box
                component="label"
                htmlFor="subcategory-image-upload"
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(0, 123, 255, 0.05)'
                  }
                }}
              >
                {imagePreview ? (
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: '100%',
                        maxHeight: 200,
                        objectFit: 'contain',
                        borderRadius: 6
                      }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.7)'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setImagePreview('');
                        setNewSubcategory({ ...newSubcategory, image: '' });
                      }}
                    >
                      <FiTrash2 size={16} />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <FiUploadCloud size={48} color="#adb5bd" style={{ marginBottom: '8px' }} />
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <Box component="span" sx={{ color: 'primary.main', fontWeight: 500 }}>
                        Click to upload
                      </Box> or drag and drop
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                      PNG, JPG, GIF (Max 2MB)
                    </Typography>
                  </>
                )}

                <input
                  id="subcategory-image-upload"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file) {
                      setNewSubcategory({ ...newSubcategory, image: file });
                      setImagePreview(URL.createObjectURL(file));
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Status Selector */}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={newSubcategory.status}
                label="Status"
                onChange={e => setNewSubcategory({ ...newSubcategory, status: e.target.value })}
                variant="outlined"
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="active">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiCircle size={14} color="#28a745" />
                    Active
                  </Box>
                </MenuItem>
                <MenuItem value="inactive">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiCircle size={14} color="#6c757d" />
                    Inactive
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Category Selector */}
            <FormControl fullWidth>
              <InputLabel>Parent Category</InputLabel>
              <Select
                value={newSubcategory.category_id}
                label="Parent Category"
                onChange={e => setNewSubcategory({ ...newSubcategory, category_id: e.target.value })}
                variant="outlined"
                sx={{ borderRadius: '8px' }}
              >
                {categories.filter(cat => cat && cat._id && cat.title).map(cat => (
                  <MenuItem key={cat._id} value={cat._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <FiLayers size={14} color="#6c757d" />
                      {cat.title}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 2 }}>
          <Button
            onClick={handleAddSubCategoryClose}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 500,
              color: 'grey',
              borderColor: "grey"
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddSubcategorySubmit}
            disabled={!newSubcategory.title || !newSubcategory.category_id}
            sx={{
              border: '1px solid black',
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 500,
              boxShadow: 'none',
              background: 'rgb(255 209 2)',
              color: 'black',
              '&:hover': {
                background: 'rgb(255 209 2)'
              },
              '&:disabled': {
                background: '#e9ecef',
                color: '#adb5bd'
              }
            }}
          >
            Create Subcategory
          </Button>
        </DialogActions>
      </Dialog>


      <Box p={2} component={Paper} boxShadow={'none'} sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'Mulish, sans-serif', }}>
            {isEdit ? (formMode === 'edit' ? 'Edit Product' : 'View Product') : (activeStep === steps.length - 1 ? 'Review Product' : 'Add New Product')}
          </Typography>

          <Box sx={{ fontFamily: 'Mulish, sans-serif', gap: 1 }} >
            {isEdit && handleFormModeChange && (
              <ToggleButton
                value={formMode}
                onClick={handleFormModeChange}
                aria-label={formMode === 'view' ? 'switch to edit mode' : 'switch to view mode'}
                size="small"
                sx={{
                  mb: 2,
                  mr: 2,
                  fontFamily: 'Mulish, sans-serif',
                  color: 'black',
                  background: 'rgba(0, 0, 0, 0.08)',
                  '&:hover': { background: 'rgba(0, 0, 0, 0.08)' }
                }}
              >
                {formMode === 'view' ? (
                  <>
                    <FaEdit style={{ marginRight: 8 }} />
                    Edit
                  </>
                ) : (
                  <>
                    <FaEye style={{ marginRight: 8 }} />
                    View
                  </>
                )}
              </ToggleButton>
            )}
            <Button
              variant="contained"
              onClick={onCancel}
              sx={{
                mb: 2, color: "white", background: 'red',
                '&:hover': {
                  bgcolor: 'red'
                }
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2} sx={{ fontFamily: 'Mulish, sans-serif', }}>
          {activeStep === steps.length - 1 ?
            'Review all details before submitting' :
            `${steps[activeStep]}`}
        </Typography>



        <Box pt={2} display="flex" sx={{
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 },
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          {/* Vertical Stepper on the left - Hidden on review page */}
          {activeStep < steps.length - 1 && (
            <Box sx={{
              width: { xs: '100%', md: 240 },
              mr: { xs: 0, md: 4 },
              flexShrink: 0,
              background: '#fcfcfc',
              borderRadius: '16px',
              p: 2,
              border: '1px solid #f0f0f0'
            }}>
              <Stepper
                activeStep={activeStep}
                orientation="vertical"
                connector={<Box sx={{ ml: 2, height: 20, borderLeft: '2px solid #eee' }} />}
                sx={{
                  '& .MuiStep-root': { mb: 1 },
                  '& .MuiStepLabel-label': {
                    fontFamily: 'Mulish, sans-serif',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    '&.Mui-active': { color: 'black', fontWeight: 700 },
                    '&.Mui-completed': { color: 'text.secondary' }
                  }
                }}
              >
                {steps.map((label, index) => {
                  const icons = [
                    <MdInfo />, 
                    <MdCategory />, 
                    <MdCurrencyRupee />, 
                    <MdScale />, 
                    <MdSettings />, 
                    <MdStar />
                  ];
                  return (
                    <Step key={label}>
                      <StepLabel
                        StepIconComponent={({ active, completed }) => (
                          <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: active ? 'rgb(255 209 2)' : (completed ? '#4caf50' : '#f0f0f0'),
                            color: active ? 'black' : (completed ? 'white' : 'text.secondary'),
                            transition: 'all 0.3s ease',
                            boxShadow: active ? '0 4px 10px rgba(255, 209, 2, 0.4)' : 'none',
                            zIndex: 1
                          }}>
                            {completed ? <MdCheckCircle size={18} /> : icons[index]}
                          </Box>
                        )}
                      >
                        {label}
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </Box>
          )}

          {/* Form content - Full width on review page */}
          <Box flex={1} sx={{
            minWidth: 0,
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden'
          }}>
            {renderStepContent(activeStep)}

            {/* <Divider  /> */}

            <Box display="flex" justifyContent="space-between" sx={{ 
              mt: 4, 
              pt: 3, 
              borderTop: '1px solid #eee' 
            }}>
              <Button
                onClick={activeStep === 0 ? onCancel : handleBack}
                variant="outlined"
                sx={{
                  borderRadius: '12px',
                  px: 4,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#666',
                  borderColor: '#ddd',
                  '&:hover': {
                    borderColor: '#999',
                    background: '#f5f5f5'
                  }
                }}
              >
                {activeStep === 0 ? 'Cancel' : 'Back'}
              </Button>

              <Box>
                {activeStep < steps.length - 1 ? (
                  <Button
                    onClick={handleNext}
                    variant="contained"
                    endIcon={<MdChevronRight />}
                    sx={{
                      borderRadius: '12px',
                      px: 5,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 700,
                      background: "rgb(255 209 2)",
                      color: 'black',
                      boxShadow: '0 4px 14px rgba(255, 209, 2, 0.4)',
                      border: '1px solid black',
                      '&:hover': {
                        background: "rgb(255 209 2)",
                        boxShadow: '0 6px 20px rgba(255, 209, 2, 0.5)',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    Next Step
                  </Button>
                ) : (
                  formMode === 'edit' && (
                    <Button
                      onClick={onSubmit}
                      variant="contained"
                      sx={{
                        borderRadius: '12px',
                        px: 5,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 700,
                        background: 'rgb(255 209 2)',
                        color: 'black',
                        border: '1px solid black',
                        boxShadow: '0 4px 14px rgba(255, 209, 2, 0.4)',
                        '&:hover': {
                          background: 'rgb(255 209 2)',
                          boxShadow: '0 6px 20px rgba(255, 209, 2, 0.5)',
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      {isEdit ? 'Update Product' : 'Confirm & Submit'}
                    </Button>
                  )
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductForm;